import React from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from './cn'
import { IconButton } from './icon-button'

/**
 * App 面的統計形狀（design-guideline §3.5，設計側 css/kiao-mobile-sleek.css 的 `.s-*`）：
 * 數字與單位、這個月的大數字區、對照條、兩欄、區段標、最近七天的點。
 * 只排版，不懂資料：數字怎麼算、怎麼格式化由頁面決定（格式見 `@kiao-voice/core` 的
 * `durationParts`）。
 */

export interface StatPart {
  value: string
  unit: string
}

/** lg 是頁面唯一的大數字；md 是分欄與連續天數；sm 是語音輸入頁今天的數字與用量。 */
export type StatAmountSize = 'lg' | 'md' | 'sm'

export interface StatAmountProps {
  /** null：沒有數字可寫（還沒載到、別的月份），畫「—」。 */
  parts: StatPart[] | null
  size: StatAmountSize
  /** 數字也用灰（新用戶的 0 天）。 */
  muted?: boolean
  className?: string
}

const AMOUNT_SIZE: Record<StatAmountSize, { number: string; unit: string }> = {
  lg: { number: 'text-[length:var(--fs-m-stat)] leading-none', unit: 'ml-1 text-md' },
  md: { number: 'text-[length:var(--fs-m-stat-md)] leading-[1.1]', unit: 'ml-1 text-sm' },
  sm: { number: 'text-[length:var(--fs-m-stat-sm)] leading-[1.15]', unit: 'ml-0.75 text-sm' },
}

/** 數字 Geist 300 等寬、墨色；單位內文字、灰色小字：「18小時 58分」。 */
export function StatAmount({ parts, size, muted = false, className }: StatAmountProps) {
  const sizes = AMOUNT_SIZE[size]
  return (
    <span
      className={cn(
        'inline-block whitespace-nowrap font-[family-name:var(--font-num)] font-light tabular-nums tracking-[-0.02em]',
        sizes.number,
        muted ? 'text-muted' : 'text-foreground',
        className
      )}
    >
      {parts === null
        ? '—'
        : parts.map((part, index) => (
            <React.Fragment key={index}>
              {index > 0 ? ' ' : null}
              <span>{part.value}</span>
              {part.unit ? (
                <span
                  className={cn(
                    'font-[family-name:var(--font-body)] font-normal tracking-normal text-muted',
                    sizes.unit
                  )}
                >
                  {part.unit}
                </span>
              ) : null}
            </React.Fragment>
          ))}
    </span>
  )
}

/** 大數字的位置換成一句話（新用戶）：襯線 500。 */
export function StatLead({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <p
      className={cn(
        'm-0 font-display text-[length:var(--fs-m-display)] font-medium leading-[1.35] [font-variant-numeric:lining-nums]',
        className
      )}
    >
      {children}
    </p>
  )
}

/** 數字底下或區段裡的一行灰色補充（12px）。 */
export function StatNote({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return <p className={cn('m-0 text-xs text-muted', className)}>{children}</p>
}

/** 一行：左邊數字，右邊別的東西（連續天數與七顆點）。 */
export function StatLine({
  amount,
  end,
  className,
}: {
  amount: React.ReactNode
  end?: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex items-center justify-between gap-3', className)}>
      {amount}
      {end}
    </div>
  )
}

export interface MonthStepButton {
  label: string
  onClick: () => void
  disabled?: boolean
}

export interface MonthStatProps {
  /** 「九月省下的時間」。 */
  label: React.ReactNode
  previous: MonthStepButton
  next: MonthStepButton
  /** 大數字，或新用戶的那一句。 */
  figure: React.ReactNode
  /** 大數字下一行。 */
  note?: React.ReactNode
  /** 對照條。 */
  children?: React.ReactNode
  className?: string
}

/** 這個月的大數字區：月份與左右切換、大數字、下一行、對照條。 */
export function MonthStat({
  label,
  previous,
  next,
  figure,
  note,
  children,
  className,
}: MonthStatProps) {
  return (
    <div className={className}>
      <div className="-mr-2.5 flex items-center justify-between">
        <span className="text-sm text-muted">{label}</span>
        <span className="flex">
          <IconButton
            variant="bar"
            aria-label={previous.label}
            disabled={previous.disabled}
            onClick={previous.onClick}
          >
            <ChevronLeft aria-hidden="true" />
          </IconButton>
          <IconButton
            variant="bar"
            aria-label={next.label}
            disabled={next.disabled}
            onClick={next.onClick}
          >
            <ChevronRight aria-hidden="true" />
          </IconButton>
        </span>
      </div>
      <div className="mt-1.5">{figure}</div>
      {note != null ? <p className="m-0 mt-2 text-sm text-muted">{note}</p> : null}
      {children != null ? <div className="mt-5">{children}</div> : null}
    </div>
  )
}

export interface ComparisonBarLegend {
  label: React.ReactNode
  value: React.ReactNode
}

export interface ComparisonBarProps {
  /** 墨色段佔整條的比例，0…1；0 時整條淡色。 */
  inkFraction: number
  start: ComparisonBarLegend
  end: ComparisonBarLegend
  className?: string
}

/**
 * 對照條：整條是一個總量，前面淡色段、後面墨色段，條下左右各一個標籤。淡色段最短 3px，
 * 很短時仍看得到；沒有墨色段時整條淡色。條本身不念，數字在標籤裡。
 */
export function ComparisonBar({ inkFraction, start, end, className }: ComparisonBarProps) {
  const ink = Number.isFinite(inkFraction) ? Math.min(1, Math.max(0, inkFraction)) : 0
  const segment = 'block h-full basis-0 rounded-[1px]'
  return (
    <div className={className}>
      <div aria-hidden="true" className="flex h-[var(--meter-h)] gap-0.5">
        <i
          className={cn(segment, 'min-w-0.75 bg-[var(--meter-track)]')}
          style={{ flexGrow: ink > 0 ? 1 - ink : 1 }}
        />
        {ink > 0 ? (
          <i className={cn(segment, 'bg-[var(--meter-fill)]')} style={{ flexGrow: ink }} />
        ) : null}
      </div>
      <div className="mt-2 flex justify-between gap-3 text-xs tabular-nums text-muted">
        <ComparisonLegend {...start} />
        <ComparisonLegend {...end} />
      </div>
    </div>
  )
}

function ComparisonLegend({ label, value }: ComparisonBarLegend) {
  return (
    <span>
      {label} <b className="font-medium text-foreground">{value}</b>
    </span>
  )
}

export interface StatColumn {
  label: React.ReactNode
  amount: React.ReactNode
  detail: React.ReactNode
}

export interface StatColumnsProps {
  columns: [StatColumn, StatColumn]
  className?: string
}

/** 兩欄（語音輸入｜會議逐字稿）：上緣一條髮絲線，各欄標籤、大字、一行補充。 */
export function StatColumns({ columns, className }: StatColumnsProps) {
  return (
    <div className={cn('grid grid-cols-2 border-t border-[var(--line)]', className)}>
      {columns.map((column, index) => (
        <div key={index} className={cn('min-w-0 pt-4', index > 0 && 'pl-5')}>
          <span className="block text-sm text-muted">{column.label}</span>
          <span className="mt-1.5 block">{column.amount}</span>
          <span className="mt-1 block truncate text-xs tabular-nums text-muted">
            {column.detail}
          </span>
        </div>
      ))}
    </div>
  )
}

export interface StatRowItem {
  amount: React.ReactNode
  label: React.ReactNode
}

export interface StatRowProps {
  items: StatRowItem[]
  className?: string
}

/** 一排並列的數字（推薦獎勵的三個數）：上緣一條髮絲線，等寬分欄，數字在上、標籤在下。 */
export function StatRow({ items, className }: StatRowProps) {
  return (
    <div
      className={cn('grid border-t border-[var(--line)]', className)}
      style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}
    >
      {items.map((item, index) => (
        <div key={index} className="min-w-0 pb-1 pt-3.5">
          <span className="block">{item.amount}</span>
          <span className="mt-0.5 block text-xs text-muted">{item.label}</span>
        </div>
      ))}
    </div>
  )
}

export interface StatHeadingProps {
  title: React.ReactNode
  /** 標題後的灰色計數（「6 / 8」）。 */
  count?: React.ReactNode
  /** 右側灰字；裡面的 `<b>` 是墨色。 */
  aside?: React.ReactNode
  /** 18px（成就）；預設 15px（連續使用、說話速度）。 */
  large?: boolean
  as?: 'h2' | 'h3'
  className?: string
}

/** 統計頁的區段標：標題（可帶計數）與右側一段灰字。本身不帶外距。 */
export function StatHeading({
  title,
  count,
  aside,
  large = false,
  as: Heading = 'h3',
  className,
}: StatHeadingProps) {
  return (
    <div
      className={cn(
        'flex justify-between gap-3',
        large ? 'items-baseline' : 'items-center',
        className
      )}
    >
      <Heading className={cn('m-0 font-semibold', large ? 'text-xl' : 'text-md')}>
        {title}
        {count != null ? <span className="ml-1.5 font-normal text-muted">{count}</span> : null}
      </Heading>
      {aside != null ? (
        <span className="text-sm text-muted [&_b]:font-semibold [&_b]:text-foreground">
          {aside}
        </span>
      ) : null}
    </div>
  )
}

/** 一天的點：有用、沒用、今天還沒用。 */
export type WeekDot = 'used' | 'unused' | 'today'

export interface WeekDotsProps {
  days: WeekDot[]
  /** 整排念一句（「最近七天有 5 天使用」）。 */
  'aria-label': string
  className?: string
}

const DOT: Record<WeekDot, string> = {
  used: 'bg-foreground',
  unused: 'shadow-[inset_0_0_0_1.5px_var(--line-strong)]',
  today: 'shadow-[inset_0_0_0_1.5px_var(--brand)]',
}

/** 最近幾天的點：有用墨色實心、沒用淡描邊、今天還沒用紅色描邊。 */
export function WeekDots({ days, className, ...rest }: WeekDotsProps) {
  return (
    <span
      role="img"
      aria-label={rest['aria-label']}
      className={cn('flex items-center gap-2.5', className)}
    >
      {days.map((day, index) => (
        <i key={index} className={cn('block size-2 rounded-full', DOT[day])} />
      ))}
    </span>
  )
}
